import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useDivision } from "@/context/DivisionContext";
import { CheckCircle, FileText } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CompletedProjects = () => {
  const { division } = useDivision();
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const { getAuthHeaders } = useAuth();

  useEffect(() => {
    fetchArchivedProjects();
  }, [division]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = projects.filter(p => 
        p.project_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.control_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.contractor_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredProjects(filtered);
    } else {
      setFilteredProjects(projects);
    }
  }, [searchTerm, projects]);

  const fetchArchivedProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`, { 
        headers: getAuthHeaders(),
        params: { division }
      });
      // Filter for paid/completed projects (status = "paid")
      const completedProjects = response.data.filter(p => p.status === 'paid');
      setProjects(completedProjects);
      setFilteredProjects(completedProjects);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching completed projects:", error);
      toast.error("Failed to load completed projects");
      setLoading(false);
    }
  };

  const generateInvoice = async (project) => {
    try {
      const invoiceUrl = `${API}/projects/${project.id}/invoice`;
      window.open(invoiceUrl, '_blank');
    } catch (error) {
      console.error("Error generating invoice:", error);
      toast.error("Failed to generate invoice");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US');
  };

  if (loading) {
    return <div className="text-white p-8">Loading...</div>;
  }

  const totalValue = filteredProjects.reduce((sum, p) => sum + (p.bid_amount || 0), 0);

  return (
    <div className="ml-64 p-8 min-h-screen bg-gradient-to-br from-[#0f172a] to-[#1e293b]">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2 flex items-center gap-3">
              <CheckCircle className="w-10 h-10 text-[#10b981]" />
              Completed Projects
            </h1>
            <p className="text-[#94a3b8]">Finished and paid projects</p>
          </div>
          <div className="bg-[#1e293b] p-6 rounded-lg border-2 border-[#10b981]">
            <div className="text-xs text-[#94a3b8] uppercase tracking-wider mb-1">Total Completed Value</div>
            <div className="text-3xl font-bold text-[#10b981]">{formatCurrency(totalValue)}</div>
            <div className="text-xs text-[#10b981] mt-1">{filteredProjects.length} projects</div>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <Input
            type="text"
            placeholder="Search by project name, control number, or contractor..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="millmetrix-input max-w-md"
          />
        </div>

        {filteredProjects.length === 0 ? (
          <Card className="millmetrix-card p-12 text-center">
            <CheckCircle className="w-16 h-16 text-[#10b981] mx-auto mb-4" />
            <p className="text-[#94a3b8] text-lg">
              {searchTerm ? 'No projects match your search' : 'No completed projects yet'}
            </p>
            <p className="text-[#64748b] text-sm mt-2">
              {searchTerm ? 'Try a different search term' : 'Finished and paid projects will appear here'}
            </p>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredProjects.map((project) => (
              <Card key={project.id} className="millmetrix-card p-6 hover:border-[#10b981] transition-all">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-2">
                      {project.project_name}
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                      <div>
                        <span className="text-[#94a3b8]">Control #:</span>
                        <span className="text-white ml-2 font-mono">{project.control_number}</span>
                      </div>
                      <div>
                        <span className="text-[#94a3b8]">Contractor:</span>
                        <span className="text-white ml-2">{project.contractor_name}</span>
                      </div>
                      <div>
                        <span className="text-[#94a3b8]">Final Amount:</span>
                        <span className="text-[#10b981] ml-2 font-bold">{formatCurrency(project.bid_amount)}</span>
                      </div>
                      <div>
                        <span className="text-[#94a3b8]">Completed:</span>
                        <span className="text-white ml-2">{formatDate(project.created_at)}</span>
                      </div>
                    </div>
                    <div className="flex gap-6 text-xs text-[#64748b]">
                      <span>Days on site: {project.days_on_site || 0}</span>
                      <span>Total LF: {(project.total_lf || 0).toLocaleString()}</span>
                      <span>Margin: {(project.estimated_margin || 0).toFixed(2)}%</span>
                    </div>
                  </div>
                  <div className="ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => generateInvoice(project)}
                      className="border-[#10b981] text-[#10b981] hover:bg-[#10b981] hover:text-white"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      View Invoice
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CompletedProjects;
